/**
 * Deal Readiness Journey Component
 *
 * A staged unlock system that guides sellers through the deal-readiness journey:
 * - Stage 1: Profile Setup - Basic visibility to buyers
 * - Stage 2: Match Optimization - Smart matching algorithm
 * - Stage 3: Deal Ready - Express due diligence
 * - Stage 4: Premium Visibility - Featured placement
 *
 * Each stage unlocks specific capabilities, motivating completion.
 */

import { useState, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  CheckCircle2,
  Circle,
  Lock,
  Unlock,
  ChevronRight,
  ChevronDown,
  Building2,
  FileText,
  Target,
  Users,
  Sparkles,
  TrendingUp,
  Zap,
  Award,
  Eye,
  Calendar,
  MessageSquare,
  Star,
  ArrowRight,
  Gift,
} from 'lucide-react';

// Stage Definitions - Aligned with BD priorities
const STAGES = [
  {
    id: 1,
    name: 'Profile Setup',
    description: 'Must-have basics for BD screening',
    icon: Building2,
    color: 'blue',
    unlocks: 'Visible to pharma BD teams',
    unlocksDetail: 'Your profile appears in buyer searches with essential info',
    tasks: [
      {
        id: 'company_name',
        label: 'Add company name',
        check: (data) => !!data.company?.company_name,
      },
      {
        id: 'has_assets',
        label: 'Add at least one pipeline asset',
        check: (data) => data.assets?.length > 0,
      },
      {
        id: 'asset_stage',
        label: 'Specify development stage for each asset',
        check: (data) => {
          if (!data.assets?.length) return false;
          return data.assets.every(a => !!a.stage);
        },
      },
      {
        id: 'asset_indication',
        label: 'Add indication for each asset',
        check: (data) => {
          if (!data.assets?.length) return false;
          return data.assets.every(a => !!a.indication);
        },
      },
    ],
  },
  {
    id: 2,
    name: 'Get Discovered',
    description: 'Nice-to-have details that help buyers decide',
    icon: Eye,
    color: 'purple',
    unlocks: 'Priority in search results + smart matching',
    unlocksDetail: 'Buyers can filter and find you based on differentiation',
    tasks: [
      {
        id: 'differentiation',
        label: 'Describe asset differentiation',
        check: (data) => {
          if (!data.assets?.length) return false;
          return data.assets.some(a => a.differentiation?.length > 20);
        },
      },
      {
        id: 'clinical_data',
        label: 'Add clinical data summaries',
        check: (data) => {
          if (!data.assets?.length) return false;
          return data.assets.some(a => a.clinical_data?.length > 20);
        },
      },
      {
        id: 'deal_preferences',
        label: 'Specify deal preferences (license, co-develop, M&A)',
        check: (data) => {
          // Check asset-level deal preferences first, then company-level
          if (data.assets?.some(a => a.deal_preferences?.length > 0)) return true;
          return !!data.company?.deal_preferences?.length;
        },
      },
      {
        id: 'therapeutic_areas',
        label: 'Select therapeutic areas',
        check: (data) => data.company?.therapeutic_areas?.length > 0,
      },
    ],
  },
  {
    id: 3,
    name: 'Deal Ready',
    description: 'Accelerate partnership conversations',
    icon: Zap,
    color: 'green',
    unlocks: 'Meeting requests + express due diligence',
    unlocksDetail: 'Buyers can request meetings knowing you are prepared',
    tasks: [
      {
        id: 'nda_template',
        label: 'Upload NDA template',
        helpUrl: 'https://www.cooleygo.com/documents/form-bilateral-nda/',
        helpText: 'Get free bilateral NDA template from Cooley GO',
        check: (data) => !!data.company?.documents?.nda_template,
      },
      {
        id: 'corporate_deck',
        label: 'Upload corporate presentation (10-15 pages)',
        helpUrl: 'https://www.ycombinator.com/library/4T-how-to-design-a-better-pitch-deck',
        helpText: 'See YC Biotech Deck Guide for best practices',
        check: (data) => !!data.company?.documents?.corporate_deck,
      },
      {
        id: 'teaser',
        label: 'Upload non-confidential teaser (1-2 pages)',
        helpText: 'A brief summary that can be shared before NDA signing',
        check: (data) => !!data.company?.documents?.teaser,
      },
      {
        id: 'term_sheet_expectations',
        label: 'Define term sheet expectations for assets',
        helpText: 'Set upfront, milestones, territory, and royalty expectations',
        check: (data) => {
          // Check asset-level structured term sheet expectations
          if (data.assets?.some(a =>
            a.upfront_range || a.territory_rights?.length > 0 || a.milestone_range || a.royalty_range
          )) return true;
          // Fallback to text-based expectations
          if (data.assets?.some(a => a.term_sheet_expectations?.length > 10)) return true;
          return false;
        },
      },
      {
        id: 'website',
        label: 'Add company website',
        check: (data) => !!data.company?.website,
      },
    ],
  },
  {
    id: 4,
    name: 'Premium Profile',
    description: 'DD-ready documents for serious discussions',
    icon: Star,
    color: 'amber',
    unlocks: 'Featured placement + accelerated due diligence',
    unlocksDetail: 'Pre-uploaded DD documents speed up partner evaluation',
    tasks: [
      {
        id: 'investigator_brochure',
        label: "Upload Investigator's Brochure (IB)",
        helpUrl: 'https://www.fda.gov/media/146167/download',
        helpText: 'Core clinical document - see FDA IB Guidance',
        check: (data) => !!data.company?.documents?.investigator_brochure,
      },
      {
        id: 'clinical_data_package',
        label: 'Upload clinical data package (CSR/topline)',
        helpUrl: 'https://database.ich.org/sites/default/files/E3_Guideline.pdf',
        helpText: 'Follow ICH E3 format for clinical study reports',
        check: (data) => !!data.company?.documents?.clinical_data_package,
      },
      {
        id: 'patent_summary',
        label: 'Upload patent portfolio summary',
        helpText: 'Include granted/pending patents, expiry dates, FTO status',
        check: (data) => !!data.company?.documents?.patent_summary,
      },
      {
        id: 'cmc_overview',
        label: 'Upload CMC overview',
        helpUrl: 'https://www.fda.gov/regulatory-information/search-fda-guidance-documents',
        helpText: 'Manufacturing process, suppliers, capacity info',
        check: (data) => !!data.company?.documents?.cmc_overview,
      },
      {
        id: 'regulatory_status',
        label: 'Upload regulatory filing status',
        helpUrl: 'https://www.fda.gov/drugs/types-applications/investigational-new-drug-ind-application',
        helpText: 'IND/CTA approvals and regulatory correspondence',
        check: (data) => !!data.company?.documents?.regulatory_status,
      },
    ],
  },
];

// Color utilities
const STAGE_COLORS = {
  blue: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
    icon: 'text-blue-500',
    progress: 'bg-blue-500',
    badge: 'bg-blue-100 text-blue-700',
  },
  purple: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
    icon: 'text-purple-500',
    progress: 'bg-purple-500',
    badge: 'bg-purple-100 text-purple-700',
  },
  green: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    icon: 'text-green-500',
    progress: 'bg-green-500',
    badge: 'bg-green-100 text-green-700',
  },
  amber: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    icon: 'text-amber-500',
    progress: 'bg-amber-500',
    badge: 'bg-amber-100 text-amber-700',
  },
};

// Progress Bar Component
function StageProgressBar({ completed, total, color }) {
  const percentage = total > 0 ? (completed / total) * 100 : 0;
  const colors = STAGE_COLORS[color];

  return (
    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
      <div
        className={`h-full ${colors.progress} transition-all duration-500 ease-out`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
}

// Task types - asset-level vs company-level
const ASSET_LEVEL_TASKS = [
  'asset_stage',
  'asset_indication',
  'differentiation',
  'clinical_data',
  'deal_preferences',
  'term_sheet_expectations',
];

// Document upload task IDs
const DOCUMENT_TASKS = [
  'nda_template', 'corporate_deck', 'teaser',
  'investigator_brochure', 'clinical_data_package', 'patent_summary', 'cmc_overview', 'regulatory_status'
];

// Single Task Item with help resources
function TaskItem({ task, isComplete, isLocked, onAction }) {
  const location = useLocation();
  const navigate = useNavigate();

  const handleClick = () => {
    if (isLocked) return;

    const isOnProfilePage = location.pathname === '/seller/profile';
    const isAssetTask = ASSET_LEVEL_TASKS.includes(task.id);
    const isDocumentTask = DOCUMENT_TASKS.includes(task.id);

    if (isOnProfilePage) {
      // Pass task.id so the parent can handle appropriately
      onAction?.(task.id, isAssetTask, isDocumentTask);
    } else {
      // Navigate with task info in URL
      if (isDocumentTask) {
        navigate('/seller/profile?tab=documents&scrollTo=' + task.id);
      } else if (isAssetTask) {
        navigate('/seller/profile?action=edit_asset&task=' + task.id);
      } else {
        navigate('/seller/profile?edit=true&scrollTo=' + task.id);
      }
    }
  };

  return (
    <div className="space-y-1">
      <button
        onClick={handleClick}
        disabled={isLocked}
        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left ${
          isLocked
            ? 'bg-gray-50 cursor-not-allowed opacity-60'
            : isComplete
            ? 'bg-green-50 hover:bg-green-100'
            : 'bg-white hover:bg-gray-50 border border-gray-200 hover:border-purple-300'
        }`}
      >
        {isLocked ? (
          <Lock className="w-5 h-5 text-gray-300 flex-shrink-0" />
        ) : isComplete ? (
          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
        ) : (
          <Circle className="w-5 h-5 text-gray-300 flex-shrink-0" />
        )}
        <span
          className={`flex-1 text-sm ${
            isComplete ? 'text-green-700 line-through' : isLocked ? 'text-gray-400' : 'text-gray-700'
          }`}
        >
          {task.label}
        </span>
        {!isLocked && !isComplete && (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
      </button>
      {/* Help text and resource link */}
      {!isLocked && !isComplete && (task.helpText || task.helpUrl) && (
        <div className="ml-8 flex items-center gap-2 text-xs text-gray-500">
          <span className="text-amber-500">💡</span>
          <span>{task.helpText}</span>
          {task.helpUrl && (
            <a
              href={task.helpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600 hover:text-purple-700 hover:underline font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              Get template →
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// Stage Card Component
function StageCard({ stage, stageData, isUnlocked, isCurrent, previousCompleted, alwaysVisible = false, onEditMode }) {
  const [isExpanded, setIsExpanded] = useState(isCurrent);
  const Icon = stage.icon;
  const colors = STAGE_COLORS[stage.color];

  const { completedTasks, totalTasks, isComplete } = stageData;
  // All stages can now be expanded to show tasks (but tasks may be locked)
  const canExpand = alwaysVisible || isUnlocked || previousCompleted;

  return (
    <div
      className={`rounded-xl border-2 transition-all ${
        isComplete
          ? 'border-green-300 bg-green-50'
          : isCurrent
          ? `${colors.border} ${colors.bg}`
          : isUnlocked
          ? 'border-gray-200 bg-white'
          : 'border-gray-200 bg-gray-50'
      }`}
    >
      {/* Stage Header */}
      <button
        onClick={() => canExpand && setIsExpanded(!isExpanded)}
        disabled={!canExpand}
        className={`w-full p-4 flex items-center gap-4 ${canExpand ? 'cursor-pointer' : 'cursor-not-allowed'}`}
      >
        {/* Stage Icon */}
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
            isComplete
              ? 'bg-green-100'
              : isCurrent
              ? `${colors.bg}`
              : isUnlocked
              ? 'bg-gray-100'
              : 'bg-gray-100'
          }`}
        >
          {isComplete ? (
            <CheckCircle2 className="w-6 h-6 text-green-600" />
          ) : !isUnlocked && !previousCompleted ? (
            <Lock className="w-6 h-6 text-gray-400" />
          ) : (
            <Icon className={`w-6 h-6 ${isCurrent ? colors.icon : 'text-gray-400'}`} />
          )}
        </div>

        {/* Stage Info */}
        <div className="flex-1 text-left">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-400">STAGE {stage.id}</span>
            {isComplete && (
              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                Complete
              </span>
            )}
            {isCurrent && !isComplete && (
              <span className={`px-2 py-0.5 text-xs font-medium ${colors.badge} rounded-full`}>
                Current
              </span>
            )}
            {!isComplete && !isCurrent && !isUnlocked && (
              <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 rounded-full">
                Upcoming
              </span>
            )}
          </div>
          <h3 className={`font-semibold ${isComplete || isCurrent ? 'text-gray-900' : 'text-gray-500'}`}>
            {stage.name}
          </h3>
          <p className="text-sm text-gray-500">{stage.description}</p>
        </div>

        {/* Progress / Expand Button */}
        <div className="flex items-center gap-3">
          {canExpand && (
            <>
              <span className="text-sm font-medium text-gray-600">
                {completedTasks}/{totalTasks}
              </span>
              <ChevronDown
                className={`w-5 h-5 text-gray-400 transition-transform ${
                  isExpanded ? 'rotate-180' : ''
                }`}
              />
            </>
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && canExpand && (
        <div className="px-4 pb-4 space-y-4">
          {/* Progress Bar */}
          <div className="pl-16">
            <StageProgressBar
              completed={completedTasks}
              total={totalTasks}
              color={stage.color}
            />
          </div>

          {/* Tasks */}
          <div className="pl-16 space-y-2">
            {stage.tasks.map((task) => (
              <TaskItem
                key={task.id}
                task={task}
                isComplete={stageData.taskStatus[task.id]}
                isLocked={!isUnlocked && !previousCompleted}
                onAction={onEditMode}
              />
            ))}
          </div>

          {/* Unlock Reward */}
          {!isComplete && (
            <div className="pl-16">
              <div className={`flex items-center gap-2 p-3 rounded-lg ${colors.bg} ${colors.border} border`}>
                <Gift className={`w-5 h-5 ${colors.icon} flex-shrink-0`} />
                <div>
                  <p className={`text-sm font-medium ${colors.text}`}>{stage.unlocks}</p>
                  {stage.unlocksDetail && (
                    <span className="text-xs text-gray-500">{stage.unlocksDetail}</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Overall Progress Summary
function ProgressSummary({ overallScore, currentStage, stagesCompleted }) {
  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-purple-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-500';
  };

  const getScoreMessage = (score) => {
    if (score >= 90) return 'Excellent! Ready for serious discussions';
    if (score >= 70) return 'Good progress! A few more steps to stand out';
    if (score >= 50) return 'Building up! Complete more to attract buyers';
    return 'Getting started! Complete the basics first';
  };

  return (
    <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-6 text-white">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-5 h-5" />
            <h2 className="text-lg font-bold">Deal Readiness Journey</h2>
          </div>
          <p className="text-purple-100 text-sm">{getScoreMessage(overallScore)}</p>
        </div>

        {/* Score Circle */}
        <div className="relative w-24 h-24">
          <svg className="w-24 h-24 transform -rotate-90">
            <circle
              cx="48"
              cy="48"
              r="40"
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="8"
              fill="none"
            />
            <circle
              cx="48"
              cy="48"
              r="40"
              stroke="white"
              strokeWidth="8"
              fill="none"
              strokeDasharray={`${overallScore * 2.51} 251`}
              strokeLinecap="round"
              className="transition-all duration-700"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-bold">{overallScore}%</span>
          </div>
        </div>
      </div>

      {/* Stage Progress Dots */}
      <div className="mt-4 flex items-center justify-between">
        {STAGES.map((stage, index) => (
          <div key={stage.id} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                index < stagesCompleted
                  ? 'bg-white text-purple-600'
                  : index === stagesCompleted
                  ? 'bg-white/30 text-white border-2 border-white'
                  : 'bg-white/10 text-white/50'
              }`}
            >
              {index < stagesCompleted ? (
                <CheckCircle2 className="w-5 h-5" />
              ) : (
                <span className="text-sm font-bold">{stage.id}</span>
              )}
            </div>
            {index < STAGES.length - 1 && (
              <div
                className={`w-12 h-1 mx-1 rounded ${
                  index < stagesCompleted ? 'bg-white' : 'bg-white/20'
                }`}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Main Component
export function DealReadiness({
  company,
  assets = [],
  compact = false,
  showSuggestions = true,
  maxSuggestions = 3,
  onEditMode,
  className = '',
}) {
  // Calculate stage status
  const { stageDataList, currentStageIndex, overallScore, stagesCompleted } = useMemo(() => {
    const data = { company, assets };

    let totalWeight = 0;
    let earnedWeight = 0;
    const stageDataList = [];
    let currentStageIndex = 0;
    let stagesCompleted = 0;

    STAGES.forEach((stage, index) => {
      const taskStatus = {};
      let completed = 0;

      stage.tasks.forEach((task) => {
        const isComplete = task.check(data);
        taskStatus[task.id] = isComplete;
        if (isComplete) {
          completed++;
          earnedWeight += 1;
        }
        totalWeight += 1;
      });

      const isStageComplete = completed === stage.tasks.length;
      if (isStageComplete) {
        stagesCompleted++;
      }

      stageDataList.push({
        completedTasks: completed,
        totalTasks: stage.tasks.length,
        isComplete: isStageComplete,
        taskStatus,
      });

      // Set current stage to first incomplete stage
      if (!isStageComplete && currentStageIndex === 0 && index === stagesCompleted) {
        currentStageIndex = index;
      }
    });

    // If all stages complete, current is last
    if (stagesCompleted === STAGES.length) {
      currentStageIndex = STAGES.length - 1;
    }

    const score = totalWeight > 0 ? Math.round((earnedWeight / totalWeight) * 100) : 0;

    return {
      stageDataList,
      currentStageIndex,
      overallScore: score,
      stagesCompleted,
    };
  }, [company, assets]);

  // Compact version
  if (compact) {
    return (
      <Link
        to="/seller/profile"
        className={`flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-100 hover:shadow-md transition-all ${className}`}
      >
        <div className="relative w-12 h-12">
          <svg className="w-12 h-12 transform -rotate-90">
            <circle cx="24" cy="24" r="20" stroke="#E5E7EB" strokeWidth="4" fill="none" />
            <circle
              cx="24"
              cy="24"
              r="20"
              stroke="#8B5CF6"
              strokeWidth="4"
              fill="none"
              strokeDasharray={`${overallScore * 1.26} 126`}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs font-bold text-purple-600">{overallScore}%</span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">Deal Ready</span>
            {overallScore < 100 && (
              <span className="px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded">
                Stage {stagesCompleted + 1}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 truncate">
            {stagesCompleted === STAGES.length
              ? 'Profile complete!'
              : `Complete Stage ${stagesCompleted + 1} to unlock more`}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-400" />
      </Link>
    );
  }

  // Full version
  return (
    <div className={`space-y-4 ${className}`}>
      {/* Progress Summary Header */}
      <ProgressSummary
        overallScore={overallScore}
        currentStage={STAGES[currentStageIndex]}
        stagesCompleted={stagesCompleted}
      />

      {/* Stage Cards */}
      <div className="space-y-3">
        {STAGES.map((stage, index) => {
          const stageData = stageDataList[index];
          const previousComplete = index === 0 || stageDataList[index - 1].isComplete;
          // All stages are now visible (can be expanded to see tasks)
          // But tasks are locked if prerequisites aren't met
          const isUnlocked = previousComplete;
          const isCurrent = index === currentStageIndex && !stageData.isComplete;
          // Stage 4 (and beyond) always visible to show full path
          const alwaysVisible = true;

          return (
            <StageCard
              key={stage.id}
              stage={stage}
              stageData={stageData}
              isUnlocked={isUnlocked}
              isCurrent={isCurrent}
              previousCompleted={previousComplete}
              alwaysVisible={alwaysVisible}
              onEditMode={onEditMode}
            />
          );
        })}
      </div>

      {/* All Complete Celebration */}
      {stagesCompleted === STAGES.length && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Award className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="font-bold text-gray-900 mb-1">Congratulations!</h3>
          <p className="text-sm text-gray-600 mb-4">
            Your profile is fully complete and optimized for partner discussions.
          </p>
          <Link
            to="/seller/matching"
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
          >
            Find Matching Buyers
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </div>
  );
}

// Context-aware hint component for use in other pages
export function DealReadinessHint({
  company,
  assets = [],
  context = 'general',
  className = '',
}) {
  const { overallScore, currentStage } = useMemo(() => {
    const data = { company, assets };

    let totalWeight = 0;
    let earnedWeight = 0;
    let currentStageIndex = 0;
    let stagesCompleted = 0;

    STAGES.forEach((stage, index) => {
      let completed = 0;
      stage.tasks.forEach((task) => {
        if (task.check(data)) {
          completed++;
          earnedWeight += 1;
        }
        totalWeight += 1;
      });

      if (completed === stage.tasks.length) {
        stagesCompleted++;
      }
    });

    currentStageIndex = Math.min(stagesCompleted, STAGES.length - 1);

    return {
      overallScore: totalWeight > 0 ? Math.round((earnedWeight / totalWeight) * 100) : 0,
      currentStage: STAGES[currentStageIndex],
    };
  }, [company, assets]);

  if (overallScore >= 80) return null;

  const contextMessages = {
    contact: {
      title: 'Improve your response rate',
      description: 'Buyers are more likely to respond to complete profiles',
    },
    matching: {
      title: 'Get better matches',
      description: 'Complete your profile for more accurate buyer recommendations',
    },
    meeting: {
      title: 'Prepare for discussions',
      description: 'A complete profile helps set the right expectations',
    },
    general: {
      title: 'Boost your visibility',
      description: 'Complete profiles get more buyer attention',
    },
  };

  const message = contextMessages[context] || contextMessages.general;

  return (
    <Link
      to="/seller/profile"
      className={`flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200 hover:bg-amber-100 transition-colors ${className}`}
    >
      <div className="flex items-center justify-center w-10 h-10 bg-amber-100 rounded-full flex-shrink-0">
        <span className="text-sm font-bold text-amber-700">{overallScore}%</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-amber-800">{message.title}</p>
        <p className="text-xs text-amber-600">{message.description}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-amber-500" />
    </Link>
  );
}

export default DealReadiness;
